import { enumMembers, type ApiSchema } from "../apiSchema";
import {
  readArray,
  readBoolean,
  readInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";
import { readReviewAction, type ReviewAction } from "./reviewActions";
import { readFindingMessage, readMergeBlocks, type MergeBlocks } from "./mergeBlockDecoders";
import { readTermLabel, type TermLabel } from "./ontologyApi";
import { readHistoricalReview, type HistoricalReview } from "./historicalReview";
import { readItemBumps, readNullableVersionBump, type VersionBump } from "./versionDecoders";

export type { CheckFinding, MergeBlocks, ShapeViolation, ShapeViolations } from "./mergeBlockDecoders";

/**
 * The review of a change set as the server sends it (`server/src/dmw/api/routes/content/
 * review_bodies.py`, `docs/components/reviews.md`), decoded into the web app's types.
 */

export type ItemKind = ApiSchema<"ProjectItemKind">;
export type ChangeSetState = ApiSchema<"ChangeSetState">;
export type CheckStatus = ApiSchema<"CheckStatus">;
export type ConflictLevel = ApiSchema<"ConflictLevel">;
export type TermChangeKind = ApiSchema<"TermChangeKind">;
export type FindingKind = ApiSchema<"FindingKind">;
export type DecisionKind = ApiSchema<"ReviewDecisionKind">;
export type FileChangeKind = ApiSchema<"FileChangeKind">;

export const ITEM_KINDS = enumMembers<ItemKind>({
  few_shot_example: true,
  competency_question: true,
  shapes: true,
  label_list: true,
  ontology_module: true,
  prompt: true,
  step: true,
  workflow: true,
  tool: true,
  skill: true,
  artifact: true,
  capability_overrides: true,
});
export const CONFLICT_LEVELS = enumMembers<ConflictLevel>({ conflict: true, check: true, info: true });
const CHANGE_SET_STATES = enumMembers<ChangeSetState>({ open: true, sent_back: true, merged: true, scrapped: true });
export const CHECK_STATUSES = enumMembers<CheckStatus>({ pass: true, fail: true, cannot_run: true, not_checked: true });
const TERM_CHANGE_KINDS = enumMembers<TermChangeKind>({
  removed: true,
  renamed: true,
  added: true,
  signature_changed: true,
  hierarchy_changed: true,
  label_changed: true,
  definition_changed: true,
  other_changed: true,
});
const FINDING_KINDS = enumMembers<FindingKind>({ term_used: true, unused_term_changed: true, check_regressed: true, possible_duplicate: true });
const DECISION_KINDS = enumMembers<DecisionKind>({ approve: true, request_changes: true, scrap: true, merge: true });
const FILE_CHANGE_KINDS = enumMembers<FileChangeKind>({ added: true, modified: true, removed: true });

export interface ItemLocation {
  readonly kind: ItemKind;
  readonly itemId: string;
}

export interface HistoryEntry {
  readonly event: string;
  readonly actor: string;
  readonly at: string;
  readonly reason: string;
}

export interface ChangeSetSummary {
  readonly changeSetId: string;
  readonly projectId: string;
  readonly title: string;
  readonly state: ChangeSetState;
  readonly openedBy: string;
  readonly openedAt: string;
  readonly history: readonly HistoryEntry[];
  /** `<thread>#<number>` of the entries it was saved from, if any. */
  readonly originThreadEntries: readonly string[];
}

export interface TripleChanges {
  readonly added: readonly string[];
  readonly removed: readonly string[];
  readonly addedCount: number;
  readonly removedCount: number;
  readonly touchedTerms: readonly string[];
}

export interface ItemChange {
  readonly kind: ItemKind;
  readonly itemId: string;
  readonly change: FileChangeKind;
  readonly name: string;
  readonly oldReference: string | null;
  readonly newReference: string | null;
  readonly triples: TripleChanges | null;
  readonly changedFields: readonly string[] | null;
}

/** What a reasoning check ran, as the engine names it (checks.md): a pass shows no more. */
export interface RuleSet {
  readonly name: string;
  readonly rules: readonly string[];
}

export interface CheckResult {
  readonly checkId: string;
  readonly kind: string;
  readonly status: CheckStatus;
  /** What the check is, in words: a competency question's text, a shapes item's or test data's name (H-01); `null` for the engine's own checks and in results stored before titles. */
  readonly title: string | null;
  readonly summary: string;
  readonly findingMessages: readonly string[];
  /** Every finding the check found; `findingMessages` lists at most the engine's limit, a stored report fewer. */
  readonly findingCount: number;
  /** `null` for shapes and questions, and in runs stored before the engine named its rules. */
  readonly ruleSet: RuleSet | null;
}

export interface CheckRun {
  readonly checkRunId: string;
  /** Of the model's and the process graph's checks together. */
  readonly status: CheckStatus;
  readonly results: readonly CheckResult[];
  /** The process graph's checks (process-governance.md PG-3); `null` if none ran. */
  readonly processResults: readonly CheckResult[] | null;
}

export interface FindingItem {
  readonly kind: ItemKind;
  readonly itemId: string;
  readonly name: string;
  readonly reference: string;
}

export interface ConflictFinding {
  readonly message?: string | null;
  readonly level: ConflictLevel;
  readonly kind: FindingKind;
  readonly item: FindingItem | null;
  readonly term: string | null;
  readonly termChangeKinds: readonly TermChangeKind[];
  readonly renamedTo: string | null;
  readonly via: string | null;
  readonly checkId: string | null;
}

export interface ConflictCheck {
  readonly conflictCheckId: string;
  readonly rulesVersion: string;
  readonly baseChecked: boolean;
  readonly counts: Readonly<Record<ConflictLevel, number>>;
  readonly findings: readonly ConflictFinding[];
  /** Labels of changed terms by IRI, each with its language, for plain-language names (`conflictTermNames`). */
  readonly termLabels: Readonly<Record<string, readonly TermLabel[]>>;
}

export interface OpenFindings {
  readonly checkRunMissing: boolean;
  readonly failingCheckIds: readonly string[];
  readonly notRunnableCheckIds: readonly string[];
  readonly conflictCheckMissing: boolean;
  readonly conflictFindingCount: number;
  /** Open possible duplicates at or above the review threshold (curation-ui.md CU-6). */
  readonly openCandidateCount: number;
  /** Competency questions the change set adds or changes that check nothing (reviews.md R-3). */
  readonly uncheckedQuestionIds: readonly string[];
  readonly needsAcknowledgement: boolean;
}


export interface Decision {
  readonly decisionId: string;
  readonly kind: DecisionKind;
  readonly reviewer: string;
  readonly outdated: boolean;
  readonly coveredItems: readonly ItemLocation[];
  readonly comment: string;
  readonly acknowledgementNote: string | null;
  /** For a merge: the change line and the bumps the person merging confirmed (ADR-0033 D3). */
  readonly changeLine: string;
  /** For the project content as a whole. */
  readonly versionBump: VersionBump | null;
  readonly itemBumps: Readonly<Record<string, VersionBump>>;
  /** For a merge: who approved the merged head before it; empty when the merge was the only approval. */
  readonly mergeApprovers: readonly string[] | null;
  readonly at: string;
}

export interface ReviewSummary {
  readonly coverage: readonly { readonly item: ItemLocation; readonly approvedBy: readonly string[] }[];
  readonly uncovered: readonly ItemLocation[];
  /** The changed items the caller curates: what they may decide on and mark. */
  readonly callerCurates: readonly ItemLocation[];
  /** What the caller's approval counts for: four eyes takes out their own executable items (R-2). */
  readonly callerCovered: readonly ItemLocation[];
}

/** The Thread step that decides a change set (ADR-0031 D4); `null` for one made outside any Workflow. */
export interface ReviewSubject {
  readonly threadId: string;
  readonly stepKey: string;
}

function readReviewSubject(value: unknown): ReviewSubject | null {
  if (value === null || value === undefined) {
    return null;
  }
  const subject = readObject(value, "review subject");
  return { threadId: readString(subject, "thread_id"), stepKey: readString(subject, "step_key") };
}

export interface Review {
  /** The account owner's display name, including installation accounts outside the member list. */
  readonly proposerName?: string | null;
  readonly historicalReview?: HistoricalReview | null;
  readonly reviewSubject: ReviewSubject | null;
  readonly changeSet: ChangeSetSummary;
  readonly head: string;
  readonly items: readonly ItemChange[];
  readonly checks: CheckRun | null;
  readonly conflictCheck: ConflictCheck | null;
  /** Newest first. */
  readonly decisions: readonly Decision[];
  readonly summary: ReviewSummary;
  readonly openFindings: OpenFindings;
  /** The longest comment or reason a decision takes: the server's `content.maximum_comment_characters`. */
  readonly maximumCommentCharacters: number;
  readonly mergeBlocks: MergeBlocks;
  /** What the caller may do now, and why not, as module B decides it. */
  readonly actions: readonly ReviewAction[];
}

export function readItemLocation(value: unknown): ItemLocation {
  const item = readObject(value, "item");
  return { kind: readOneOf(item, "kind", ITEM_KINDS), itemId: readString(item, "item_id") };
}

export function readChangeSetSummary(value: unknown): ChangeSetSummary {
  const body = readObject(value, "change set");
  return {
    changeSetId: readString(body, "change_set_id"),
    projectId: readString(body, "project_id"),
    title: readString(body, "title"),
    state: readOneOf(body, "state", CHANGE_SET_STATES),
    openedBy: readString(body, "opened_by"),
    openedAt: readString(body, "opened_at"),
    history: readArray(body["history"], "history", (item) => {
      const entry = readObject(item, "history entry");
      return {
        event: readString(entry, "event"),
        actor: readString(entry, "actor"),
        at: readString(entry, "at"),
        reason: readString(entry, "reason"),
      };
    }),
    originThreadEntries: readStringList(body, "origin_thread_entries"),
  };
}

function readItemChange(value: unknown): ItemChange {
  const item = readObject(value, "item change");
  const triples = item["triples"] === null ? null : readObject(item["triples"], "triples");
  return {
    kind: readOneOf(item, "kind", ITEM_KINDS),
    itemId: readString(item, "item_id"),
    change: readOneOf(item, "change", FILE_CHANGE_KINDS),
    name: readString(item, "name"),
    oldReference: readNullableString(item, "old"),
    newReference: readNullableString(item, "new"),
    triples:
      triples === null
        ? null
        : {
            added: readStringList(triples, "added_triples"),
            removed: readStringList(triples, "removed_triples"),
            addedCount: readInteger(triples, "added_count"),
            removedCount: readInteger(triples, "removed_count"),
            touchedTerms: readStringList(triples, "touched_terms"),
          },
    changedFields: item["changed_fields"] === null ? null : readStringList(item, "changed_fields"),
  };
}

/** A check run, as the review serves it and as a check step's `check_report` value holds it. */
export function readCheckRun(value: unknown): CheckRun {
  const run = readObject(value, "check run");
  return {
    checkRunId: readString(run, "check_run_id"),
    status: readOneOf(run, "status", CHECK_STATUSES),
    results: readCheckResults(run["results"]),
    processResults: readProcessResults(run),
  };
}

/** The review serves `process: {results}` (or null); a `check_report` value holds
 * `process_results` only when the process graph was checked. */
function readProcessResults(run: JsonObject): readonly CheckResult[] | null {
  const section = run["process"];
  if (section !== undefined && section !== null) {
    return readCheckResults(readObject(section, "process checks")["results"]);
  }
  return run["process_results"] === undefined ? null : readCheckResults(run["process_results"]);
}

export function readCheckResults(value: unknown): readonly CheckResult[] {
  return readArray(value, "check results", (item) => {
    const result = readObject(item, "check result");
    return {
      checkId: readString(result, "check_id"),
      kind: readString(result, "kind"),
      status: readOneOf(result, "status", CHECK_STATUSES),
      title: result["title"] === undefined ? null : readNullableString(result, "title"),
      summary: readString(result, "summary"),
      findingMessages: readArray(result["findings"], "findings", readFindingMessage),
      findingCount: readInteger(result, "finding_count"),
      ruleSet: readRuleSet(result["rule_set"]),
    };
  });
}

function readRuleSet(value: unknown): RuleSet | null {
  if (value === undefined || value === null) return null;
  const ruleSet = readObject(value, "rule set");
  return { name: readString(ruleSet, "name"), rules: readStringList(ruleSet, "rules") };
}

function readCounts(object: JsonObject, member: string): Record<ConflictLevel, number> {
  const counts = readObject(object[member], `"${member}"`);
  return {
    conflict: readInteger(counts, "conflict"),
    check: readInteger(counts, "check"),
    info: readInteger(counts, "info"),
  };
}

function readConflictCheck(value: unknown): ConflictCheck {
  const run = readObject(value, "conflict check");
  const termLabels: Record<string, readonly TermLabel[]> = {};
  for (const change of readArray(run["term_changes"], "term changes", (item) => readObject(item, "term change"))) {
    termLabels[readString(change, "iri")] = readArray(change["labels"], "labels", readTermLabel);
  }
  return {
    conflictCheckId: readString(run, "conflict_check_id"),
    rulesVersion: readString(run, "rules_version"),
    baseChecked: readBoolean(run, "base_checked"),
    counts: readCounts(run, "counts"),
    termLabels,
    findings: readArray(run["findings"], "conflict findings", (item) => {
      const finding = readObject(item, "conflict finding");
      const findingItem = finding["item"] === null ? null : readObject(finding["item"], "finding item");
      return {
        level: readOneOf(finding, "level", CONFLICT_LEVELS),
        kind: readOneOf(finding, "kind", FINDING_KINDS),
        item:
          findingItem === null
            ? null
            : {
                kind: readOneOf(findingItem, "kind", ITEM_KINDS),
                itemId: readString(findingItem, "item_id"),
                name: readString(findingItem, "name"),
                reference: readString(findingItem, "reference"),
              },
        term: readNullableString(finding, "term"),
        termChangeKinds: readArray(finding["term_change_kinds"], "term change kinds", (kind) =>
          readOneOf({ kind }, "kind", TERM_CHANGE_KINDS),
        ),
        renamedTo: readNullableString(finding, "renamed_to"),
        via: readNullableString(finding, "via"),
        checkId: readNullableString(finding, "check_id"),
        message: finding["message"] === undefined ? null : readNullableString(finding, "message"),
      };
    }),
  };
}

function readOpenFindings(value: unknown): OpenFindings {
  const findings = readObject(value, "open findings");
  return {
    checkRunMissing: readBoolean(findings, "check_run_missing"),
    failingCheckIds: readStringList(findings, "failing_check_ids"),
    notRunnableCheckIds: readStringList(findings, "not_runnable_check_ids"),
    conflictCheckMissing: readBoolean(findings, "conflict_check_missing"),
    conflictFindingCount: readInteger(findings, "conflict_finding_count"),
    openCandidateCount: findings["open_candidate_count"] === undefined ? 0 : readInteger(findings, "open_candidate_count"),
    uncheckedQuestionIds: findings["unchecked_question_ids"] === undefined ? [] : readStringList(findings, "unchecked_question_ids"),
    needsAcknowledgement: readBoolean(findings, "needs_acknowledgement"),
  };
}

function readDecision(value: unknown): Decision {
  const decision = readObject(value, "decision");
  const acknowledgement =
    decision["acknowledgement"] === null ? null : readObject(decision["acknowledgement"], "acknowledgement");
  return {
    decisionId: readString(decision, "decision_id"),
    kind: readOneOf(decision, "kind", DECISION_KINDS),
    reviewer: readString(decision, "reviewer"),
    outdated: readBoolean(decision, "outdated"),
    coveredItems: readArray(decision["covered_items"], "covered items", readItemLocation),
    comment: readString(decision, "comment"),
    acknowledgementNote: acknowledgement === null ? null : readString(acknowledgement, "note"),
    changeLine: typeof decision["release_note"] === "string" ? decision["release_note"] : "",
    versionBump: readNullableVersionBump(decision, "version_bump"),
    itemBumps: readItemBumps(decision, "item_bumps"),
    mergeApprovers: decision["merge_approvers"] == null ? null : readStringList(decision, "merge_approvers"),
    at: readString(decision, "at"),
  };
}

export function readReview(value: unknown): Review {
  const body = readObject(value, "review");
  const view = readObject(body["review"], "change set view");
  const summary = readObject(body["summary"], "review summary");
  return {
    historicalReview: readHistoricalReview(body["historical_review"]),
    reviewSubject: readReviewSubject(body["review_subject"]),
    changeSet: readChangeSetSummary(view["change_set"]),
    head: readString(view, "head"),
    items: readArray(view["items"], "item changes", readItemChange),
    checks: view["checks"] === null ? null : readCheckRun(view["checks"]),
    conflictCheck: body["conflict_check"] === null ? null : readConflictCheck(body["conflict_check"]),
    decisions: readArray(body["decisions"], "decisions", readDecision),
    summary: {
      coverage: readArray(summary["coverage"], "coverage", (item) => {
        const coverage = readObject(item, "item coverage");
        return { item: readItemLocation(coverage["item"]), approvedBy: readStringList(coverage, "approved_by") };
      }),
      uncovered: readArray(summary["uncovered"], "uncovered items", readItemLocation),
      callerCurates: readArray(summary["caller_curates"], "caller's items", readItemLocation),
      callerCovered: readArray(summary["caller_covered"], "caller's counted items", readItemLocation),
    },
    openFindings: readOpenFindings(body["open_findings"]),
    maximumCommentCharacters: readInteger(body, "maximum_comment_characters"),
    proposerName: body["proposer_name"] === undefined ? null : readNullableString(body, "proposer_name"),
    mergeBlocks: readMergeBlocks(body["merge_blocks"]),
    actions: readArray(body["actions"], "review actions", readReviewAction),
  };
}

export interface ReviewListEntry {
  readonly changeSet: ChangeSetSummary;
  readonly head: string;
  readonly touchedCount: number;
  readonly uncoveredCount: number;
  readonly checkStatus: CheckStatus | null;
  readonly conflictCounts: Readonly<Record<ConflictLevel, number>> | null;
  readonly callerApprovedHead: boolean;
  readonly lastDecisionAt: string | null;
  readonly reviewSubject: ReviewSubject | null;
}

export function readReviewListEntry(value: unknown): ReviewListEntry {
  const entry = readObject(value, "review list entry");
  return {
    changeSet: readChangeSetSummary(entry["change_set"]),
    head: readString(entry, "head"),
    touchedCount: readInteger(entry, "touched_count"),
    uncoveredCount: readInteger(entry, "uncovered_count"),
    checkStatus: entry["check_status"] === null ? null : readOneOf(entry, "check_status", CHECK_STATUSES),
    conflictCounts: entry["conflict_counts"] === null ? null : readCounts(entry, "conflict_counts"),
    callerApprovedHead: readBoolean(entry, "caller_approved_head"),
    lastDecisionAt: readNullableString(entry, "last_decision_at"),
    reviewSubject: readReviewSubject(entry["review_subject"]),
  };
}
